import {
  createStyles,
  Theme,
  type WithStyles,
  withStyles,
} from "@material-ui/core/styles";
import { useTranslations } from "adapters/i18n";
import React from "react";

type PermissionInfoCardProps = WithStyles<typeof styles>;

function PermissionInfoCard({ classes }: PermissionInfoCardProps) {
  const { t } = useTranslations();

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <span className={classes.headerTitle}>
          {t("user:permissions-user-will-have")}
        </span>
        <Caption classes={classes} />
      </div>
      <div className={classes.content} data-testid="permission-info-rows">
        <PermissionInfoRow
          id="can-access-platform"
          label={t("user:permissions.can-access-platform")}
          type="needs-approval"
        />
        <PermissionInfoRow
          id="can-access-app"
          label={t("user:permissions.can-access-app")}
          type="needs-approval"
        />
        <PermissionInfoRow
          id="final-stock"
          label={t("user:permissions.final-stock")}
          type="needs-approval"
        />
        <PermissionInfoRow
          id="import-maps"
          label={t("user:permissions.import-maps")}
          type="needs-approval"
        />
        <PermissionInfoRow
          id="export-csv"
          label={t("user:permissions.export-csv")}
          type="needs-approval"
        />
        <PermissionInfoRow
          id="register-forage-cycle"
          label={t("user:permissions.register-user")}
          type="blocked"
        />
        <PermissionInfoRow
          id="edit-user"
          label={t("user:edit-user")}
          type="blocked"
        />
        <PermissionInfoRow
          id="inactivate-user"
          label={t("user:inactivate-user")}
          type="blocked"
        />
        <PermissionInfoRow
          id="create-retreats"
          label={t("locales:create-retreats")}
          type="allowed"
        />
        <PermissionInfoRow
          id="link-retreats-to-users"
          label={t("locales:link-retreats-to-users")}
          type="blocked"
        />
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <img
      alt="check icon"
      data-testid="check"
      src="/images/check.svg"
      width={20}
      height={20}
    />
  );
}

function WarningIcon() {
  return (
    <img
      alt="warning icon"
      data-testid="warning"
      src="/images/warning.svg"
      width={20}
      height={20}
    />
  );
}

function BlockIcon() {
  return (
    <img
      alt="block icon"
      data-testid="block"
      src="/images/block.svg"
      width={20}
      height={20}
    />
  );
}

function DottedLine() {
  return <img alt="dotted line" src="/images/dotted-line.svg" width="100%" />;
}

type CaptionProps = WithStyles<typeof styles>;

function Caption({ classes }: CaptionProps) {
  const { t } = useTranslations();

  return (
    <div className={classes.caption}>
      <div>
        <CheckIcon />
        <span>{t("user:permissions.have-permission")}</span>
      </div>
      <div>
        <BlockIcon />
        <span>{t("user:permissions.do-not-have-permission")}</span>
      </div>
      <div>
        <WarningIcon />
        <span>{t("user:permissions.needs-clearance")}</span>
      </div>
    </div>
  );
}

type PermissionInfoRowProps = {
  id: string;
  label: string;
  type: "allowed" | "blocked" | "needs-approval";
};

function PermissionInfoRow({ id, label, type }: PermissionInfoRowProps) {
  const typeToComponent = {
    allowed: () => <CheckIcon />,
    blocked: () => <BlockIcon />,
    "needs-approval": () => <WarningIcon />,
  };

  const IconComponent = typeToComponent[type];

  return (
    <div data-testid={`permission-info:${id}`}>
      <span>{label}</span>
      <DottedLine />
      {IconComponent != null && <IconComponent />}
    </div>
  );
}

const styles = (theme: Theme) =>
  createStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      gap: "32px",
      padding: "16px",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "8px",
      backgroundColor: "#F6FAFE",
    },
    header: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
    },
    headerTitle: {
      fontFamily: "Roboto",
      fontSize: "20px",
      fontWeight: 700,
      color: "#212121",
    },
    caption: {
      display: "flex",
      flexDirection: "row",
      gap: "32px",
      "& div": {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "8px",
      },
      "& span": {
        fontFamily: "Roboto",
        fontSize: "16px",
        fontWeight: 500,
        color: "#212121",
      },
    },
    content: {
      display: "flex",
      flexDirection: "column",
      gap: "24px",
      "& div": {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "8px",
        "& span": {
          fontFamily: "Roboto",
          fontSize: "14px",
          color: "#334150",
          whiteSpace: "nowrap",
        },
      },
    },
  });

export default withStyles(styles)(PermissionInfoCard);
